import { useEffect, useState } from 'react'

// Noticing a newer deploy while a tab (or the installed app) stays open, as notesfrom does. The page names the build
// it was served with (a meta tag written at build time, see vite.config.ts) and the server publishes the latest one
// at /version.json. During development there's no tag, so this never reports anything.

export const builtVersion = (doc: Document = document) =>
  doc.querySelector<HTMLMetaElement>('meta[name="gatheredfrom-version"]')?.content || null

export async function liveVersion(fetchFn: typeof fetch = fetch) {
  try {
    const res = await fetchFn('/version.json', { cache: 'no-store', credentials: 'same-origin' })
    if (!res.ok) return null
    const data: unknown = await res.json()
    const version = (data as { version?: unknown } | null)?.version
    return typeof version === 'string' ? version : null
  } catch {
    return null // offline, or no server here
  }
}

// A different build on the server than the one running. Unknown either way means no.
export const isNewer = (running: string | null, live: string | null) => !!running && !!live && running !== live

// Work a reload would cut short, like album photos still uploading. Refreshing waits until there's none.
let working = 0
export async function whileWorking<T>(work: Promise<T>) {
  working++
  try {
    return await work
  } finally {
    working--
  }
}
export const isWorking = () => working > 0

// Resolves once nothing is being saved: no write still on its way (isSaving) and no work registered above.
export async function settled(isSaving: () => boolean, everyMs = 250) {
  while (isSaving() || isWorking()) await new Promise((r) => setTimeout(r, everyMs))
}

const EVERY_MS = 15 * 60_000 // while the page is in view
const AT_MOST_MS = 60_000 // coming back to the page checks again, but not more often than this

// True once the server has a newer build than the one this page is running.
export function useNewVersion() {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    const running = builtVersion()
    if (!running || ready) return
    let last = 0
    let alive = true
    const check = async () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < AT_MOST_MS) return
      last = Date.now()
      if (isNewer(running, await liveVersion()) && alive) setReady(true)
    }
    const timer = setInterval(check, EVERY_MS)
    document.addEventListener('visibilitychange', check)
    window.addEventListener('focus', check)
    return () => {
      alive = false
      clearInterval(timer)
      document.removeEventListener('visibilitychange', check)
      window.removeEventListener('focus', check)
    }
  }, [ready])
  return ready
}
